import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  House, 
  SignOut,
  List,
  X,
  At,
  FolderOpen,
  ShieldCheck,
  CalendarCheck,
  GearSix,
  MagnifyingGlass,
  Sparkle
} from '@phosphor-icons/react';
import { useState, useEffect } from 'react';
import IndieLawLogo from './IndieLawLogo';
import FeedbackWidget from './FeedbackWidget';
import BetaBanner from './BetaBanner';
import API_URL from '../config';

const ALL_NAV_ITEMS = [
  { key: 'search-report', path: '/search-report', label: 'Search Report', icon: MagnifyingGlass },
  { key: 'matters', path: '/dashboard', label: 'My Matters', icon: House },
  { key: 'emails', path: '/emails', label: 'Emails', icon: At },
  { key: 'documents', path: '/drive', label: 'Documents', icon: FolderOpen },
  { key: 'protection-plan', path: '/protection-plan', label: 'Protection Plan', icon: ShieldCheck },
  { key: 'book-call', path: '/book-call', label: 'Book a Call', icon: CalendarCheck },
  { key: 'whats-new', path: '/whats-new', label: "What's New", icon: Sparkle },
];
// Shown before view-state loads: only the always-safe tabs, so no GATED tab
// (Protection Plan / Welcome / Search Report) ever flashes before it's confirmed.
const DEFAULT_VISIBLE = ['matters', 'emails', 'documents', 'book-call', 'whats-new'];

export default function DashboardLayout({ children }) {
  const { user, logout, previewMode } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visibleTabs, setVisibleTabs] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/client/view-state`)
      .then((res) => {
        setVisibleTabs(res.data.visible_tabs || DEFAULT_VISIBLE);
      })
      .catch(() => setVisibleTabs(DEFAULT_VISIBLE));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeKeys = visibleTabs || DEFAULT_VISIBLE;
  const navItems = ALL_NAV_ITEMS.filter((i) => activeKeys.includes(i.key));

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      {/* Preview Banner */}
      {previewMode && (
        <div className="bg-[#FFD60A] text-[#09090B] text-center py-2 text-sm font-semibold" data-testid="preview-banner">
          PREVIEW MODE — Viewing dashboard as {user?.name}
          <button onClick={() => window.close()} className="ml-4 underline font-medium">Close Preview</button>
        </div>
      )}
      {/* Beta banner + Top Navigation — pinned together so both stay visible */}
      <div className="sticky top-0 z-50">
        <BetaBanner />
        <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="logo-link">
              <IndieLawLogo variant="dark" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'text-[#09090B] bg-[#F4F4F5]'
                      : 'text-[#52525B] hover:text-[#09090B] hover:bg-[#F4F4F5]'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon weight={isActive(item.path) ? 'duotone' : 'regular'} className="w-5 h-5" />
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* User Info & Logout */}
            <div className="flex items-center gap-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium" style={{ color: '#101820' }}>{user?.name}</p>
                <p className="text-xs" style={{ color: '#9a9488' }}>{user?.email}</p>
              </div>
              <Link to="/settings" className="p-2 hover:bg-[#f2e9db] transition-colors" title="Settings"
                style={{ color: '#9a9488' }} data-testid="settings-link">
                <GearSix weight="regular" className="w-5 h-5" />
              </Link>
              <button
                onClick={logout}
                className="p-2 hover:bg-[#f2e9db] transition-colors"
                title="Sign out"
                style={{ color: '#9a9488' }}
                data-testid="logout-btn"
              >
                <SignOut weight="regular" className="w-5 h-5" />
              </button>
              
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-[#52525B] hover:text-[#09090B]"
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? (
                  <X weight="bold" className="w-6 h-6" />
                ) : (
                  <List weight="bold" className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <nav className="md:hidden border-t border-[#E4E4E7] bg-white">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 text-sm font-medium border-b border-[#E4E4E7] ${
                  isActive(item.path)
                    ? 'text-[#09090B] bg-[#F4F4F5]'
                    : 'text-[#52525B]'
                }`}
              >
                <item.icon weight={isActive(item.path) ? 'duotone' : 'regular'} className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
          </nav>
        )}
      </header>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      <FeedbackWidget />
    </div>
  );
}
